import type { TStudioLLMBlock } from '@api/flow.api';
import { STUDIO_TYPE_LABELS } from '@conversed/shared/src/central/types/studio.type';
import { useBlocksActions } from '@hooks/flow/block/useBlocksActions';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { useEffect, useMemo, useState } from 'react';

interface UseLLMBlockSelectorOptions {
  enabled: boolean;
  sharedWithCompanyIds?: string[];
}

interface UseLLMBlockSelectorResult {
  allBlocks: TStudioLLMBlock[];
  blockTranslations: Record<string, string>;
}

export const useLLMBlockSelector = ({
  enabled,
  sharedWithCompanyIds,
}: UseLLMBlockSelectorOptions): UseLLMBlockSelectorResult => {
  const { selectedCompany } = useCompanySelector();
  const { getLLMBlocksByCompany } = useBlocksActions();
  const [allBlocks, setAllBlocks] = useState<TStudioLLMBlock[]>([]);

  const relevantCompanyIds = useMemo(
    () =>
      [selectedCompany?.id, ...(sharedWithCompanyIds ?? [])].filter(
        Boolean,
      ) as string[],
    [selectedCompany?.id, sharedWithCompanyIds],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    getLLMBlocksByCompany(relevantCompanyIds).then((data) =>
      setAllBlocks(data),
    );
  }, [enabled, getLLMBlocksByCompany, relevantCompanyIds]);

  const blockTranslations = useMemo(() => {
    const translations: Record<string, string> = {};
    for (const block of allBlocks) {
      translations[block.blockId] =
        `${STUDIO_TYPE_LABELS[block.studioType]} - ${block.botName || 'Unknown Bot'}`;
    }
    return translations;
  }, [allBlocks]);

  return { allBlocks, blockTranslations };
};
